import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';

import { UserRole, type Team, type User } from '@kangaroo/shared';
import { SectionCard } from '@kangaroo/ui';

import { completeCurrentTeamSetup, createCurrentTeam, currentTeamKey, requestTeamJoin } from '../../features/teams';
import { canCreateTeamWorkspace } from '../../lib/roles';
import { useAuthStore } from '../../store/authStore';

interface TeamOnboardingPageProps {
  user: User;
  provisionalTeam?: Team | null;
}

function getErrorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? 'Unable to create team workspace';
  }

  return error instanceof Error ? error.message : 'Unable to create team workspace';
}

const WORKSPACE_ROLE_OPTIONS = [UserRole.TEAM_OWNER, UserRole.COACH, UserRole.PARENT] as const;

function formatWorkspaceRole(role: UserRole) {
  if (role === UserRole.TEAM_OWNER) return 'Team Owner';
  if (role === UserRole.COACH) return 'Coach';
  return 'Parent';
}

export function TeamOnboardingPage({ user, provisionalTeam = null }: TeamOnboardingPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate } = useSWRConfig();
  const updateUser = useAuthStore((state) => state.updateUser);
  const clearSession = useAuthStore((state) => state.clearSession);
  const completingProvisionalSetup = Boolean(user.teamId && provisionalTeam?.workspaceSetupPending);
  const [teamName, setTeamName] = useState('');
  const [teamCode, setTeamCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole>(canCreateTeamWorkspace(user.role) ? user.role : UserRole.TEAM_OWNER);
  const initialJoinRequestSent = typeof location.state === 'object'
    && location.state !== null
    && 'joinRequestSent' in location.state
    && location.state.joinRequestSent === true
    || new URLSearchParams(location.search).get('requestSent') === '1'
    || window.sessionStorage.getItem('gridline:joinRequestSent') === user.id;
  const [successMessage, setSuccessMessage] = useState<string | null>(
    initialJoinRequestSent ? 'Request sent. A team owner or coach can accept it from Invitations.' : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const canCreateTeam = canCreateTeamWorkspace(selectedRole);
  const canRequestJoin = user.role === UserRole.DRIVER || user.role === UserRole.PARENT;
  const [workspaceMode, setWorkspaceMode] = useState<'create' | 'join'>(
    user.role === UserRole.PARENT && initialJoinRequestSent ? 'join' : canCreateTeam ? 'create' : 'join',
  );

  useEffect(() => {
    if (!completingProvisionalSetup || !provisionalTeam) {
      return;
    }

    setTeamName(provisionalTeam.name);
    setTeamCode(provisionalTeam.code);
    if (user.role === UserRole.DRIVER) {
      setSelectedRole(UserRole.DRIVER);
      setWorkspaceMode('create');
    }
  }, [completingProvisionalSetup, provisionalTeam, user.role]);

  useEffect(() => {
    if (initialJoinRequestSent) {
      window.sessionStorage.removeItem('gridline:joinRequestSent');
    }
  }, [initialJoinRequestSent]);

  useEffect(() => {
    if (workspaceMode === 'join' || completingProvisionalSetup) {
      return;
    }

    setWorkspaceMode(canCreateTeam ? 'create' : 'join');
  }, [canCreateTeam, completingProvisionalSetup, workspaceMode]);

  async function finishWorkspaceSetup(response: { team: Team; user: User }) {
    updateUser(response.user);
    await mutate(currentTeamKey, response.team, false);
    navigate('/drivers?setup=complete&billing=1', { replace: true });
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (teamName.trim().length < 2) {
      setError('Enter a team name to continue.');
      return;
    }

    if (teamCode.trim().length < 2) {
      setError('Enter a team code to continue.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        teamName: teamName.trim(),
        teamCode: teamCode.trim().toUpperCase(),
        role: selectedRole as UserRole.TEAM_OWNER | UserRole.COACH | UserRole.PARENT | UserRole.DRIVER,
      };

      if (completingProvisionalSetup) {
        await finishWorkspaceSetup(await completeCurrentTeamSetup(payload));
        return;
      }

      const response = await createCurrentTeam(payload);
      updateUser(response.user);
      await mutate(currentTeamKey, response.team, false);
      navigate('/drivers?setup=complete&billing=1', { replace: true });
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleJoinRequest(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (joinCode.trim().length < 2) {
      setError('Enter a team code to request access.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await requestTeamJoin({ teamCode: joinCode.trim().toUpperCase() });
      setSuccessMessage(`Request sent to ${response.team.name}. A team owner or coach can accept it from Invitations.`);
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  }

  function handleLogout() {
    void clearSession().then(() => navigate('/login', { replace: true }));
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', width: '100%', py: { xs: 2, md: 4 } }}>
      <SectionCard
        title="Finish Your Account Setup"
        subtitle={
          completingProvisionalSetup
            ? 'Your mobile uploads are saved. Name your workspace, pick how you use Paddiq, then set up billing.'
            : 'Choose how you will use Paddiq, then create a workspace or request access to an existing team.'
        }
        contentSx={{ p: { xs: 2, md: 3 } }}
      >
        <Stack spacing={2.5}>
          {completingProvisionalSetup ? (
            <Alert severity="info">
              Sessions uploaded from the mobile app stay in this workspace. You are finishing setup here, not starting over.
            </Alert>
          ) : null}

          <TextField
            label="Role"
            select
            value={selectedRole}
            onChange={(event) => {
              const nextRole = event.target.value as UserRole;
              setSelectedRole(nextRole);
              setError(null);
              if (nextRole === UserRole.DRIVER) {
                setWorkspaceMode('create');
              } else if (workspaceMode === 'join' && nextRole !== UserRole.PARENT) {
                setWorkspaceMode('create');
              }
            }}
            fullWidth
          >
            {WORKSPACE_ROLE_OPTIONS.map((role) => (
              <MenuItem key={role} value={role}>{formatWorkspaceRole(role)}</MenuItem>
            ))}
            <MenuItem value={UserRole.DRIVER}>Driver</MenuItem>
          </TextField>

          {user.role === UserRole.PARENT && !completingProvisionalSetup && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                type="button"
                variant={workspaceMode === 'create' ? 'contained' : 'outlined'}
                onClick={() => {
                  setWorkspaceMode('create');
                  setError(null);
                }}
                fullWidth
              >
                Create Family Team
              </Button>
              <Button
                type="button"
                variant={workspaceMode === 'join' ? 'contained' : 'outlined'}
                onClick={() => {
                  setWorkspaceMode('join');
                  setError(null);
                }}
                fullWidth
              >
                Request Team Access
              </Button>
            </Stack>
          )}

          {canCreateTeam && workspaceMode === 'create' ? (
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                  label="Team name"
                  value={teamName}
                  onChange={(event) => setTeamName(event.target.value)}
                  required
                  autoFocus
                  fullWidth
                  placeholder="Apex Vector Racing"
                />
                <TextField
                  label="Team code"
                  value={teamCode}
                  onChange={(event) => setTeamCode(event.target.value.toUpperCase())}
                  required
                  fullWidth
                  helperText="Set once. Drivers and parents use this code to request access."
                  placeholder="AVR24"
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={<AddBusinessOutlinedIcon />}
                  disabled={submitting}
                  sx={{ alignSelf: 'flex-start', fontWeight: 800 }}
                >
                  {submitting
                    ? 'Saving...'
                    : completingProvisionalSetup
                      ? 'Finish Workspace Setup'
                      : 'Create Team Workspace'}
                </Button>
              </Stack>
            </Box>
          ) : canRequestJoin && workspaceMode === 'join' && !completingProvisionalSetup ? (
            <Box component="form" onSubmit={handleJoinRequest}>
              <Stack spacing={2}>
                {error && <Alert severity="error">{error}</Alert>}
                {successMessage && <Alert severity="success">{successMessage}</Alert>}
                <TextField
                  label="Team code"
                  value={joinCode}
                  onChange={(event) => setJoinCode(event.target.value.toUpperCase())}
                  required
                  autoFocus
                  fullWidth
                  helperText={user.role === UserRole.PARENT
                    ? 'Sends a request for your parent account only. Drivers request teams separately.'
                    : 'Send a request to join a team. Your driver account stays yours.'}
                  placeholder="AVR24"
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting || Boolean(successMessage)}
                  sx={{ alignSelf: 'flex-start', fontWeight: 800 }}
                >
                  {submitting ? 'Sending...' : 'Request Team Access'}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Alert severity="info">
              Ask a team owner or coach to create your account from their Team page, then complete setup from the link they send you.
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Signed in as {user.email}
            </Typography>
            <Button variant="text" onClick={handleLogout}>Log out</Button>
          </Box>
        </Stack>
      </SectionCard>
    </Box>
  );
}
